import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { RadioButton } from './radio-button';
import {
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { TYPOGRAPHY } from '@/styles';

interface RadioOption<T> {
  label: string;
  rightElement?: React.ReactNode | null;
  subLabel?: string | null;
  value: T;
}

interface Props<T> {
  baseStyle?: StyleProp<ViewStyle>;
  borderBetweenOptions?: boolean;
  onChange: (value: T) => void;
  options: RadioOption<T>[];
  radioButtonOuterStyle?: StyleProp<ViewStyle>;
  rowStyle?: StyleProp<ViewStyle>;
  selectedValue?: string;
  subTextStyle?: StyleProp<TextStyle>;
  testID?: string;
  textStyle?: StyleProp<TextStyle>;
  title: string;
  titleStyle?: StyleProp<TextStyle>;
}

export const RadioButtonGroup = <T extends string = string>({
  baseStyle,
  borderBetweenOptions = false,
  onChange,
  options,
  radioButtonOuterStyle,
  rowStyle,
  selectedValue,
  subTextStyle,
  testID,
  textStyle,
  title,
  titleStyle,
}: Props<T>) => {
  const [styles] = useStyles();
  const optionsNum = options.length;

  return (
    <View style={[styles.base, baseStyle]}>
      <Text style={[TYPOGRAPHY.h3Bold, styles.subtitle, titleStyle]}>
        {title}
      </Text>
      {options.map((opt, i) => {
        const { label, subLabel, rightElement, value } = opt;
        return (
          <TouchableOpacity
            // Rerender options with different index positions when selected pack changes
            key={`${label}-${i}`}
            onPress={() => {
              if (value !== selectedValue) {
                onChange(value);
              }
            }}
            testID={testID}
          >
            <View
              style={[
                styles.row,
                rowStyle,
                borderBetweenOptions && i < optionsNum - 1
                  ? styles.rowBorder
                  : null,
              ]}
            >
              <View style={styles.groupedContent}>
                <View style={styles.buttonContainer}>
                  <RadioButton
                    outerStyle={radioButtonOuterStyle}
                    selected={value === selectedValue}
                  />
                </View>
                <Text style={[TYPOGRAPHY.small, textStyle]}>{label}</Text>
                <Text
                  numberOfLines={1}
                  style={[TYPOGRAPHY.xsmall, subTextStyle]}
                >
                  {subLabel}
                </Text>
              </View>
              <View style={styles.groupedContent}>{rightElement}</View>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
    paddingBottom: 12,
    paddingTop: 16,
  },
  buttonContainer: {
    marginRight: 8,
  },
  groupedContent: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 8,
  },
  rowBorder: {
    borderBottomColor: theme.colors.stroke,
    borderBottomWidth: 1,
  },
  subtitle: {
    color: theme.colors.inactiveText,
    fontSize: 14,
    marginBottom: 4,
    textTransform: 'uppercase',
  },
}));
